import { useEffect } from "react";

import { getBrandCardElementId } from "@/view/Brands/utils/brandsNavigation";

const SCROLL_RETRY_MS = 100;
const MAX_SCROLL_RETRIES = 12;

/**
 * Smooth-scrolls to a brand card after navigation (e.g. from Overview).
 * Retries until the card is in the DOM (skeleton finished, accordion expanded).
 * Re-runs when `locationHash` changes so hash-only URLs keep working.
 */
export function useScrollToBrandCard(
  brandId: number | null,
  isReady: boolean,
  locationHash = "",
): void {
  useEffect(() => {
    if (!brandId || !isReady) return;

    let retries = 0;
    let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const scrollToCard = () => {
      const element = document.getElementById(getBrandCardElementId(brandId));
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      if (retries < MAX_SCROLL_RETRIES) {
        retries += 1;
        retryTimeoutId = setTimeout(scrollToCard, SCROLL_RETRY_MS);
      }
    };

    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(scrollToCard);
    });

    return () => {
      cancelAnimationFrame(frameId);
      if (retryTimeoutId != null) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, [brandId, isReady, locationHash]);
}
